import { AlertCircleFillIcon } from "~/assets/icons/AlertCircleFillIcon";
import { CheckCircleFillIcon } from "~/assets/icons/CheckCircleFillIcon";
import { InfoCircleFillIcon } from "~/assets/icons/InfoCircleFillIcon";
import { Box, Button, Paper, Snackbar, Stack, Typography } from "@mui/material";
import type { SnackbarOrigin } from "@mui/material/Snackbar";
import type { Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

export interface ActionSnackbarAction {
  label: string;
  onClick: () => void;
  color?: string;
}

interface ActionSnackbarProps {
  toastKey?: number;
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  severity?: "success" | "error" | "info" | "warning";
  icon?: ReactNode | null;
  autoHideDuration?: number;
  anchorOrigin?: SnackbarOrigin;
  primaryAction?: ActionSnackbarAction;
  secondaryAction?: ActionSnackbarAction;
}

function getToastColors(theme: Theme) {
  const isDarkMode = theme.palette.mode === "dark";
  return {
    background: isDarkMode
      ? theme.palette.custom.gray[100]
      : theme.palette.custom.gray[900],
    title: isDarkMode
      ? theme.palette.custom.gray[900]
      : theme.palette.custom.gray[0],
    description: isDarkMode
      ? theme.palette.custom.gray[700]
      : theme.palette.custom.gray[200],
    action: theme.palette.custom.gray[500],
    iconOnError: theme.palette.common.white,
  };
}

function DefaultSeverityIcon({
  severity,
}: {
  severity: "success" | "error" | "info" | "warning";
}) {
  switch (severity) {
    case "success":
      return (
        <CheckCircleFillIcon
          aria-hidden
          sx={(theme) => ({ fontSize: 24, flexShrink: 0, color: theme.palette.custom.index.good })}
        />
      );
    case "error":
      return (
        <AlertCircleFillIcon
          aria-hidden
          sx={(theme) => ({ fontSize: 24, flexShrink: 0, color: theme.palette.custom.index.poor })}
        />
      );
    case "warning":
      return (
        <AlertCircleFillIcon
          aria-hidden
          sx={(theme) => ({ fontSize: 24, flexShrink: 0, color: theme.palette.custom.index.fair })}
        />
      );
    default:
      return (
        <InfoCircleFillIcon
          aria-hidden
          sx={(theme) => ({ fontSize: 24, flexShrink: 0, color: theme.palette.custom.gray[500] })}
        />
      );
  }
}

export default function ActionSnackbar({
  toastKey,
  open,
  onClose,
  title,
  description,
  severity = "info",
  icon,
  autoHideDuration = 10000,
  anchorOrigin = { vertical: "top", horizontal: "center" },
  primaryAction,
  secondaryAction,
}: ActionSnackbarProps) {
  const resolvedIcon =
    icon === null ? null : (icon ?? <DefaultSeverityIcon severity={severity} />);

  return (
    <Snackbar
      key={toastKey}
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
    >
      <Paper
        elevation={0}
        role="alert"
        sx={(theme) => {
          const colors = getToastColors(theme);
          return {
            backgroundColor: colors.background,
            color: colors.title,
            width: "100%",
            minWidth: { xs: 320, md: 420 },
            maxWidth: { xs: "calc(100vw - 32px)", sm: 480 },
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            borderRadius: "var(--Card-Radius-Medium)",
            boxShadow: theme.shadows[8],
          };
        }}
      >
        {resolvedIcon ? (
          <Box
            sx={{
              width: 24,
              height: 27,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {resolvedIcon}
          </Box>
        ) : null}
        <Stack
          spacing={1}
          sx={{
            flex: "1 1 auto",
            minWidth: 0,
            alignItems: "flex-start",
          }}
        >
          <Typography
            component="div"
            sx={(theme) => {
              const colors = getToastColors(theme);
              return {
                color: colors.title,
                fontWeight: 600,
                fontSize: 20,
                lineHeight: 1.33,
                letterSpacing: 0,
              };
            }}
          >
            {title}
          </Typography>
          {description ? (
            <Typography
              component="div"
              variant="body2"
              sx={(theme) => {
                const colors = getToastColors(theme);
                return {
                  color: colors.description,
                  fontWeight: 400,
                  fontSize: 14,
                  lineHeight: 1.5,
                  letterSpacing: 0,
                };
              }}
            >
              {description}
            </Typography>
          ) : null}
          {primaryAction || secondaryAction ? (
            <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
              {primaryAction ? (
                <Button
                  size="small"
                  variant="text"
                  onClick={primaryAction.onClick}
                  sx={(theme) => ({
                    color: primaryAction.color ?? getToastColors(theme).action,
                    textTransform: "none",
                    minHeight: "unset !important",
                    minWidth: "auto",
                    px: 0,
                    py: 0,
                    fontWeight: 500,
                    fontSize: 14,
                    lineHeight: 1.5,
                    letterSpacing: 0,
                  })}
                >
                  {primaryAction.label}
                </Button>
              ) : null}
              {secondaryAction ? (
                <Button
                  size="small"
                  variant="text"
                  onClick={secondaryAction.onClick}
                  sx={(theme) => ({
                    color: secondaryAction.color ?? getToastColors(theme).action,
                    textTransform: "none",
                    minHeight: "unset !important",
                    minWidth: "auto",
                    px: 0,
                    py: 0,
                    fontWeight: 500,
                    fontSize: 14,
                    lineHeight: 1.5,
                    letterSpacing: 0,
                  })}
                >
                  {secondaryAction.label}
                </Button>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </Paper>
    </Snackbar>
  );
}
